enum ElDatePickerTypes {
  DATE = 'date',
  DATETIME = 'datetime',
  DATETIME_RANGE = 'datetimerange',
  DATE_RANGE = 'daterange',
}

enum FilterSources {
  TABLE = 'table',
  RAW_SQL = 'rawsql',
}

enum FilterFormats {
  CHECKBOX = 'checkbox',
  DATE = 'date',
  DATETIME = 'datetime-local',
  DROPDOWN = 'dropdownlist',
  INTERVAL = 'interval',
  RAW_SQL = 'rawsql',
  TEXT = 'text',
  TIME = 'time',
}

enum FieldValueInputTypes {
  BOOLEAN = 'boolean',
  DATE = 'date',
  DATE_INTERVAL = 'date-interval',
  DATETIME = 'datetime-local',
  DATETIME_INTERVAL = 'datetime-interval',
  DROPDOWN = 'dropdown',
  NUMBER = 'number',
  NUMBER_INTERVAL = 'number-interval',
  TEXT = 'text',
  TIME = 'time',
  TIME_INTERVAL = 'time-interval',
}

enum Operators {
  EQUALS = '=',
  NOT_EQUALS = '<>',
  LESS_THAN = '<',
  LESS_THAN_OR_EQUAL = '<=',
  GREATER_THAN = '>',
  GREATER_THAN_OR_EQUAL = '>=',
  LIKE = 'LIKE',
  NOT_LIKE = 'NOT LIKE',
  IN = 'IN',
  NOT_IN = 'NOT IN',
  BETWEEN = 'BETWEEN',
  NOT_BETWEEN = 'NOT BETWEEN',
}

enum StringFieldOperators {
  EQUALS = '=',
  NOT_EQUALS = '<>',
  LIKE = 'LIKE',
  NOT_LIKE = 'NOT LIKE',
  IN = 'IN',
  NOT_IN = 'NOT IN',
}

const StringInputTypes: Record<StringFieldOperators, FieldValueInputTypes> = {
  [Operators.EQUALS]: FieldValueInputTypes.TEXT,
  [Operators.NOT_EQUALS]: FieldValueInputTypes.TEXT,
  [Operators.LIKE]: FieldValueInputTypes.TEXT,
  [Operators.NOT_LIKE]: FieldValueInputTypes.TEXT,
  [Operators.IN]: FieldValueInputTypes.TEXT,
  [Operators.NOT_IN]: FieldValueInputTypes.TEXT,
}

enum NumberFieldOperators {
  LIKE = 'LIKE',
  NOT_LIKE = 'NOT LIKE',
  EQUALS = '=',
  NOT_EQUALS = '<>',
  LESS_THAN = '<',
  LESS_THAN_OR_EQUAL = '<=',
  GREATER_THAN = '>',
  GREATER_THAN_OR_EQUAL = '>=',
  BETWEEN = 'BETWEEN',
  NOT_BETWEEN = 'NOT BETWEEN',
  IN = 'IN',
  NOT_IN = 'NOT IN',
}

const NumberInputTypes = {
  [Operators.LIKE]: FieldValueInputTypes.TEXT,
  [Operators.NOT_LIKE]: FieldValueInputTypes.TEXT,
  [Operators.EQUALS]: FieldValueInputTypes.NUMBER,
  [Operators.NOT_EQUALS]: FieldValueInputTypes.NUMBER,
  [Operators.LESS_THAN]: FieldValueInputTypes.NUMBER,
  [Operators.LESS_THAN_OR_EQUAL]: FieldValueInputTypes.NUMBER,
  [Operators.GREATER_THAN]: FieldValueInputTypes.NUMBER,
  [Operators.GREATER_THAN_OR_EQUAL]: FieldValueInputTypes.NUMBER,
  [Operators.BETWEEN]: FieldValueInputTypes.NUMBER_INTERVAL,
  [Operators.NOT_BETWEEN]: FieldValueInputTypes.NUMBER_INTERVAL,
  [Operators.IN]: FieldValueInputTypes.TEXT,
  [Operators.NOT_IN]: FieldValueInputTypes.TEXT,
}

enum BooleanFieldOperators {
  EQUALS = '=',
  NOT_EQUALS = '<>',
}

const BooleanInputTypes = {
  [Operators.EQUALS]: FieldValueInputTypes.BOOLEAN,
  [Operators.NOT_EQUALS]: FieldValueInputTypes.BOOLEAN,
}

enum DateFieldOperators {
  EQUALS = '=',
  NOT_EQUALS = '<>',
  LESS_THAN = '<',
  LESS_THAN_OR_EQUAL = '<=',
  GREATER_THAN = '>',
  GREATER_THAN_OR_EQUAL = '>=',
  BETWEEN = 'BETWEEN',
  NOT_BETWEEN = 'NOT BETWEEN',
}

const DateInputTypes = {
  [Operators.EQUALS]: FieldValueInputTypes.DATE,
  [Operators.NOT_EQUALS]: FieldValueInputTypes.DATE,
  [Operators.LESS_THAN]: FieldValueInputTypes.DATE,
  [Operators.LESS_THAN_OR_EQUAL]: FieldValueInputTypes.DATE,
  [Operators.GREATER_THAN]: FieldValueInputTypes.DATE,
  [Operators.GREATER_THAN_OR_EQUAL]: FieldValueInputTypes.DATE,
  [Operators.BETWEEN]: FieldValueInputTypes.DATE_INTERVAL,
  [Operators.NOT_BETWEEN]: FieldValueInputTypes.DATE_INTERVAL,
}

const DateTimeInputTypes = {
  [Operators.EQUALS]: FieldValueInputTypes.DATETIME,
  [Operators.NOT_EQUALS]: FieldValueInputTypes.DATETIME,
  [Operators.LESS_THAN]: FieldValueInputTypes.DATETIME,
  [Operators.LESS_THAN_OR_EQUAL]: FieldValueInputTypes.DATETIME,
  [Operators.GREATER_THAN]: FieldValueInputTypes.DATETIME,
  [Operators.GREATER_THAN_OR_EQUAL]: FieldValueInputTypes.DATETIME,
  [Operators.BETWEEN]: FieldValueInputTypes.DATETIME_INTERVAL,
  [Operators.NOT_BETWEEN]: FieldValueInputTypes.DATETIME_INTERVAL,
}

const TimeInputTypes = {
  [Operators.EQUALS]: FieldValueInputTypes.TIME,
  [Operators.NOT_EQUALS]: FieldValueInputTypes.TIME,
  [Operators.LESS_THAN]: FieldValueInputTypes.TIME,
  [Operators.LESS_THAN_OR_EQUAL]: FieldValueInputTypes.TIME,
  [Operators.GREATER_THAN]: FieldValueInputTypes.TIME,
  [Operators.GREATER_THAN_OR_EQUAL]: FieldValueInputTypes.TIME,
  [Operators.BETWEEN]: FieldValueInputTypes.TIME_INTERVAL,
  [Operators.NOT_BETWEEN]: FieldValueInputTypes.TIME_INTERVAL,
}

enum EnumFieldOperators {
  EQUALS = '=',
  NOT_EQUALS = '<>',
  IN = 'IN',
  NOT_IN = 'NOT IN',
}

const EnumInputTypes = {
  [Operators.EQUALS]: FieldValueInputTypes.DROPDOWN,
  [Operators.NOT_EQUALS]: FieldValueInputTypes.DROPDOWN,
  [Operators.IN]: FieldValueInputTypes.DROPDOWN,
  [Operators.NOT_IN]: FieldValueInputTypes.DROPDOWN,
}

enum FieldTypes {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  DATE = 'date',
  DATETIME = 'datetime-local',
  TIME = 'time',
  ENUM = 'dropdownlist',
  TEXT = 'text',
  DOCUMENT = 'cameras',
}

interface InputTypeMapping {
  [fieldType: string]: Partial<Record<Operators, FieldValueInputTypes>>
}

const inputTypeMapping: InputTypeMapping = {
  [FieldTypes.STRING]: StringInputTypes,
  [FieldTypes.NUMBER]: NumberInputTypes,
  [FieldTypes.BOOLEAN]: BooleanInputTypes,
  [FieldTypes.DATE]: DateInputTypes,
  [FieldTypes.DATETIME]: DateTimeInputTypes,
  [FieldTypes.TIME]: TimeInputTypes,
  [FieldTypes.ENUM]: EnumInputTypes,
}

const fieldTypeMapping: Record<string, FieldTypes> = {
  BIGINT: FieldTypes.NUMBER,
  'BIGINT UNSIGNED': FieldTypes.NUMBER,
  BINARY: FieldTypes.STRING,
  BIT: FieldTypes.NUMBER,
  BLOB: FieldTypes.STRING,
  BOOL: FieldTypes.BOOLEAN,
  CHAR: FieldTypes.STRING,
  DATE: FieldTypes.DATE,
  DATETIME: FieldTypes.DATETIME,
  DECIMAL: FieldTypes.NUMBER,
  DOUBLE: FieldTypes.NUMBER,
  'DOUBLE PRECISION': FieldTypes.NUMBER,
  ENUM: FieldTypes.ENUM,
  FLOAT: FieldTypes.NUMBER,
  INT: FieldTypes.NUMBER,
  'INT UNSIGNED': FieldTypes.NUMBER,
  INTEGER: FieldTypes.NUMBER,
  'INTEGER UNSIGNED': FieldTypes.NUMBER,
  'LONG VARBINARY': FieldTypes.STRING,
  'LONG VARCHAR': FieldTypes.STRING,
  LONGBLOB: FieldTypes.STRING,
  LONGTEXT: FieldTypes.STRING,
  MEDIUMBLOB: FieldTypes.STRING,
  MEDIUMINT: FieldTypes.NUMBER,
  'MEDIUMINT UNSIGNED': FieldTypes.NUMBER,
  MEDIUMTEXT: FieldTypes.STRING,
  NUMERIC: FieldTypes.NUMBER,
  REAL: FieldTypes.NUMBER,
  SET: FieldTypes.ENUM,
  SMALLINT: FieldTypes.NUMBER,
  'SMALLINT UNSIGNED': FieldTypes.NUMBER,
  TEXT: FieldTypes.STRING,
  TIME: FieldTypes.TIME,
  TIMESTAMP: FieldTypes.DATETIME,
  TINYBLOB: FieldTypes.STRING,
  TINYINT: FieldTypes.NUMBER,
  'TINYINT UNSIGNED': FieldTypes.NUMBER,
  TINYTEXT: FieldTypes.STRING,
  VARBINARY: FieldTypes.STRING,
  VARCHAR: FieldTypes.STRING,
}

const ValuePropByInputType = {
  [FieldValueInputTypes.BOOLEAN]: 'booleanValue',
  [FieldValueInputTypes.DATE]: 'dateValue',
  [FieldValueInputTypes.DATE_INTERVAL]: 'dateIntervalValue',
  [FieldValueInputTypes.DATETIME]: 'dateValue',
  [FieldValueInputTypes.DATETIME_INTERVAL]: 'dateIntervalValue',
  [FieldValueInputTypes.DROPDOWN]: 'dropdownValue',
  [FieldValueInputTypes.NUMBER]: 'numberValue',
  [FieldValueInputTypes.NUMBER_INTERVAL]: 'numberIntervalValue',
  [FieldValueInputTypes.TEXT]: 'textValue',
  [FieldValueInputTypes.TIME]: 'dateValue',
  [FieldValueInputTypes.TIME_INTERVAL]: 'dateIntervalValue',
}

module.exports = {
  ElDatePickerTypes,
  FilterSources,
  FilterFormats,
  FieldTypes,
  FieldValueInputTypes,
  StringFieldOperators,
  NumberFieldOperators,
  BooleanFieldOperators,
  DateFieldOperators,
  EnumFieldOperators,
  fieldTypeMapping,
  inputTypeMapping,
  ValuePropByInputType,
}
